import { useState } from 'react'
import { api, type ImportVorschau } from '../net/apiClient.js'
import { ActionButton } from '../ui/ActionButton.js'
import { notify } from '../ui/Toasts.js'
import { uuidv7 } from '../net/ids.js'
import { fehlerText } from '../net/errors.js'

/**
 * Die Kundenliste aus der alten Software einlesen.
 *
 * Drei Schritte, und die Reihenfolge ist die Botschaft: Vorlage holen, Datei
 * ansehen, dann erst schreiben. Ein Import, der beim Hochladen schreibt, ist
 * einer, den niemand rückgängig macht – und hundert halbrichtige Kunden
 * räumt danach jemand von Hand auf.
 *
 * Was dieser Bildschirm bewusst NICHT tut:
 *
 *   Er rät keine Spalte, die nicht eindeutig ist. «Kontakt» kann die Person
 *   sein oder die Nummer; wer das errät, schreibt eine Telefonnummer ins
 *   Namensfeld, und das fällt erst auf dem ersten Brief auf.
 *
 *   Er importiert keine Dublette, ohne zu fragen. Zwei Mahnungen an dieselbe
 *   Adresse sind der Moment, in dem ein Betrieb der Software misstraut.
 */

export function Kundenimport(): JSX.Element {
  const [inhalt, setInhalt] = useState('')
  const [dateiname, setDateiname] = useState('')
  const [vorschau, setVorschau] = useState<ImportVorschau | null>(null)
  const [zuordnung, setZuordnung] = useState<Record<string, string | null>>({})
  const [dublettenTrotzdem, setDublettenTrotzdem] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  function vorlageHolen(): void {
    /*
      Ein Formular statt fetch: die Datei kommt mit Dateinamen und richtigem
      Inhaltstyp vom Server, und der Browser legt sie dort ab, wo der Benutzer
      seine Downloads erwartet.
    */
    window.location.href = '/customers/import/vorlage'
  }

  async function dateiLesen(datei: File): Promise<void> {
    setFehler(null)
    setVorschau(null)
    setZuordnung({})
    const text = await datei.text()
    setInhalt(text)
    setDateiname(datei.name)
  }

  async function pruefen(neueZuordnung = zuordnung): Promise<void> {
    setFehler(null)
    try {
      setVorschau(await api.kundenimportVorschau(inhalt, neueZuordnung))
    } catch (e) {
      setFehler(fehlerText(e))
    }
  }

  async function spalteSetzen(spalte: number, feld: string): Promise<void> {
    const neu = { ...zuordnung, [String(spalte)]: feld || null }
    setZuordnung(neu)
    await pruefen(neu)
  }

  async function schreiben(): Promise<void> {
    const a = await api.kundenimport(inhalt, zuordnung, dublettenTrotzdem, uuidv7())
    notify.ok(
      `${a.geschrieben} Kunden eingelesen.` +
        (a.uebersprungen > 0 ? ` ${a.uebersprungen} übersprungen.` : '')
    )
    setInhalt('')
    setDateiname('')
    setVorschau(null)
  }

  const unzugeordnet = (vorschau?.zuordnung ?? []).filter((z) => z.feld === null)

  return (
    <div id="abschnitt-kundenimport">
      <div className="o-card">
        <div className="o-card-head">
          <h3>Kunden einlesen</h3>
          <span className="o-soft">Vorlage, Vorschau, dann erst schreiben</span>
        </div>
        <div className="o-pad">
          <p className="o-soft" style={{ marginTop: 0 }}>
            Eine Liste aus Excel oder aus der alten Software. Raporto liest sie, zeigt dir, was es
            verstanden hat, und schreibt erst, wenn du bestätigst.
          </p>

          <div className="o-aktionen">
            <button type="button" className="o-btn" id="btn-import-vorlage" onClick={vorlageHolen}>
              Vorlage herunterladen
            </button>
            <label className="o-btn" htmlFor="import-datei" style={{ cursor: 'pointer' }}>
              Datei wählen
            </label>
            <input
              id="import-datei"
              type="file"
              autoComplete="off"
              accept=".csv,text/csv,text/plain"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) void dateiLesen(f)
              }}
            />
            {dateiname && <span className="o-soft">{dateiname}</span>}
            <ActionButton
              id="btn-import-pruefen"
              disabled={!inhalt}
              labels={{ idle: 'Ansehen', busy: 'Liest …', success: 'Gelesen' }}
              onAction={() => pruefen()}
            />
          </div>

          {fehler && (
            <div className="o-band alarm" id="import-fehler" style={{ marginTop: 12 }}>
              <span>{fehler}</span>
            </div>
          )}
        </div>
      </div>

      {vorschau && (
        <>
          <div className="o-card" id="import-zuordnung" style={{ marginTop: 12 }}>
            <div className="o-card-head">
              <h3>Spalten</h3>
              <span className="o-soft">
                {unzugeordnet.length === 0
                  ? 'Alle erkannt'
                  : `${unzugeordnet.length} nicht erkannt — bitte zuordnen`}
              </span>
            </div>
            <div className="o-pad">
              {/*
                Auch die erkannten Spalten stehen hier und lassen sich ändern.
                Erkannt heisst wahrscheinlich richtig, nicht sicher richtig —
                und wer es besser weiss, soll es sagen dürfen.
              */}
              <div className="o-grid o-grid-3">
                {vorschau.zuordnung.map((z) => (
                  <div className="o-field" key={z.spalte}>
                    <label htmlFor={`spalte-${z.spalte}`}>{z.ueberschrift || `Spalte ${z.spalte + 1}`}</label>
                    <select
                      id={`spalte-${z.spalte}`}
                      value={z.feld ?? ''}
                      onChange={(e) => void spalteSetzen(z.spalte, e.target.value)}
                    >
                      <option value="">— nicht übernehmen —</option>
                      {vorschau.felder.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="o-card" id="import-vorschau" style={{ marginTop: 12 }}>
            <div className="o-card-head">
              <h3>Vorschau</h3>
              <span className="o-soft">Hier wird noch nichts geschrieben</span>
            </div>
            <div className="o-pad">
              <div className="o-angabe">
                <span>Gelesene Zeilen</span>
                <strong>{vorschau.gelesen}</strong>
              </div>
              <div className="o-angabe">
                <span>Werden eingelesen</span>
                <strong>{vorschau.gueltig}</strong>
              </div>
              <div className="o-angabe">
                <span>Mit Fehler</span>
                <strong className={vorschau.fehlerhaft ? 'fehlt' : undefined}>
                  {vorschau.fehlerhaft}
                </strong>
              </div>
              <div className="o-angabe">
                <span>Mögliche Dubletten</span>
                <strong className={vorschau.dubletten ? 'fehlt' : undefined}>
                  {vorschau.dubletten}
                </strong>
              </div>

              <table className="o-tabelle" style={{ marginTop: 12 }}>
                <thead>
                  <tr>
                    <th>Zeile</th>
                    <th>Name</th>
                    <th>Ort</th>
                    <th>Befund</th>
                  </tr>
                </thead>
                <tbody>
                  {vorschau.zeilen.map((z) => (
                    <tr key={z.zeile}>
                      <td>{z.zeile}</td>
                      <td>{z.daten.name || <em className="o-soft">fehlt</em>}</td>
                      <td>
                        {[z.daten.plz, z.daten.ort].filter(Boolean).join(' ')}
                      </td>
                      <td>
                        {z.fehler.length > 0 && <span className="fehlt">{z.fehler.join(' · ')}</span>}
                        {z.fehler.length === 0 && z.dublette && (
                          <span className="fehlt">
                            Dublette zu «{z.dublette.name}» ({z.dublette.grund})
                          </span>
                        )}
                        {z.fehler.length === 0 && !z.dublette && <span className="o-soft">in Ordnung</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {vorschau.gelesen > vorschau.zeilen.length && (
                <p className="o-soft">
                  Gezeigt werden die ersten {vorschau.zeilen.length} von {vorschau.gelesen} Zeilen.
                  Eingelesen werden alle.
                </p>
              )}

              {vorschau.dubletten > 0 && (
                <label className="o-check" style={{ marginTop: 12, display: 'block' }}>
                  <input
                    type="checkbox"
                    autoComplete="off"
                    id="import-dubletten"
                    checked={dublettenTrotzdem}
                    onChange={(e) => setDublettenTrotzdem(e.target.checked)}
                  />{' '}
                  Dubletten trotzdem anlegen — ich habe sie angesehen
                </label>
              )}

              <div className="o-aktionen" style={{ marginTop: 12 }}>
                <ActionButton
                  id="btn-import-schreiben"
                  disabled={vorschau.gueltig === 0 && !dublettenTrotzdem}
                  labels={{
                    idle: `${vorschau.gueltig} Kunden einlesen`,
                    busy: 'Liest ein …',
                    success: 'Eingelesen'
                  }}
                  onAction={schreiben}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
